/**
 * Ledger: expenses (money out) and rent payments (money in) as one list of
 * rows, like the owner's Expenses tab. Pure functions, unit tested.
 */
import { categoryLabel, normalizeCategory } from "./categories.js";

const pad = (n) => String(n).padStart(2, "0");

export function monthKey(timestamp) {
  const date = new Date(timestamp);
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}`;
}

export function currentMonthKey(now = new Date()) {
  return monthKey(now.getTime());
}

export function shiftMonth(key, delta) {
  const [year, month] = key.split("-").map(Number);
  const date = new Date(year, month - 1 + delta, 1);
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}`;
}

export function monthLabel(key) {
  const [year, month] = key.split("-").map(Number);
  return new Date(year, month - 1, 1).toLocaleDateString("en-US", { month: "long", year: "numeric" });
}

/** "YYYY-MM-DD" (local) → local-noon timestamp. */
export function dayToTimestamp(day) {
  const match = String(day ?? "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12).getTime();
}

/** Timestamp → "YYYY-MM-DD" in local time (UTC-midnight values use their UTC day). */
export function timestampToDay(timestamp) {
  if (!timestamp && timestamp !== 0) return "";
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return "";
  if (date.getUTCHours() === 0 && date.getUTCMinutes() === 0 && date.getUTCSeconds() === 0) {
    return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;
  }
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export function tenantName(tenant) {
  return [tenant?.firstName, tenant?.lastName].filter(Boolean).join(" ").trim();
}

export function houseName(property) {
  return property?.name || property?.address || "House";
}

/** Merge expenses and rent payments into ledger rows, newest first. */
export function buildLedgerRows({ expenses = [], rentPayments = [], tenants = [] }) {
  const tenantsById = new Map(tenants.map((tenant) => [String(tenant._id), tenant]));
  const rows = [];
  for (const expense of expenses) {
    rows.push({
      id: String(expense._id),
      kind: "expense",
      source: expense,
      date: expense.date,
      day: timestampToDay(expense.date),
      propertyId: expense.propertyId ? String(expense.propertyId) : "",
      description: expense.title || "",
      category: normalizeCategory(expense.category),
      amount: -Math.abs(Number(expense.amount || 0)),
      notes: expense.notes || "",
      recurring: Boolean(expense.isRecurring),
      pending: false,
    });
  }
  for (const payment of rentPayments) {
    if (payment.status === "cancelled") continue;
    const tenant = payment.tenantId ? tenantsById.get(String(payment.tenantId)) : null;
    rows.push({
      id: String(payment._id),
      kind: "rent",
      source: payment,
      date: payment.paymentDate,
      day: timestampToDay(payment.paymentDate),
      propertyId: payment.propertyId ? String(payment.propertyId) : "",
      description: payment.notes || (tenant ? `Rent · ${tenantName(tenant)}` : "Rent"),
      category: "rent",
      amount: Math.abs(Number(payment.amount || 0)),
      notes: "",
      recurring: false,
      pending: payment.status !== "completed",
    });
  }
  return rows.sort((a, b) => (b.date || 0) - (a.date || 0) || a.description.localeCompare(b.description));
}

/** period: "YYYY-MM" | "ytd" | "12m" | "all" */
export function inPeriod(timestamp, period, now = new Date()) {
  if (!period || period === "all") return true;
  if (!Number.isFinite(timestamp)) return false;
  const date = new Date(timestamp);
  if (period === "ytd") return date.getFullYear() === now.getFullYear() && date <= now;
  if (period === "12m") {
    const start = new Date(now.getFullYear() - 1, now.getMonth() + 1, 1).getTime();
    return timestamp >= start && timestamp <= now.getTime() + 86400000;
  }
  return monthKey(timestamp) === period;
}

export const BUSINESS = "business";

export function filterLedgerRows(rows, { period = "all", house = "", category = "", search = "" } = {}, now = new Date()) {
  const query = search.trim().toLowerCase();
  return rows.filter((row) => {
    if (!inPeriod(row.date, period, now)) return false;
    if (house === BUSINESS ? row.propertyId : house && row.propertyId !== house) return false;
    if (category && row.category !== category) return false;
    if (query && !`${row.description} ${row.notes} ${categoryLabel(row.category)}`.toLowerCase().includes(query)) {
      return false;
    }
    return true;
  });
}

export function ledgerTotals(rows) {
  let moneyIn = 0;
  let moneyOut = 0;
  for (const row of rows) {
    if (row.pending) continue;
    if (row.amount >= 0) moneyIn += row.amount;
    else moneyOut += -row.amount;
  }
  return { count: rows.length, moneyIn, moneyOut, net: moneyIn - moneyOut };
}

/** Per-house in/out/net for one month, plus a "Business" row for unassigned expenses. */
export function monthByHouse(rows, properties, period, now = new Date()) {
  const inMonth = rows.filter((row) => !row.pending && inPeriod(row.date, period, now));
  const lines = properties.map((property) => {
    const own = inMonth.filter((row) => row.propertyId === String(property._id));
    return { id: String(property._id), name: houseName(property), ...ledgerTotals(own) };
  });
  const business = inMonth.filter((row) => !row.propertyId || !properties.some((p) => String(p._id) === row.propertyId));
  if (business.length) lines.push({ id: BUSINESS, name: "Business", ...ledgerTotals(business) });
  return { lines, total: ledgerTotals(inMonth) };
}

function csvCell(value) {
  const text = String(value ?? "");
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

export function ledgerToCsv(rows, propertiesById) {
  const header = ["Date", "House", "Description", "Category", "Amount", "Notes"];
  const lines = rows.map((row) => [
    row.day,
    row.propertyId ? houseName(propertiesById.get(row.propertyId)) : "Business",
    row.description,
    categoryLabel(row.category),
    row.amount.toFixed(2),
    row.notes,
  ]);
  return [header, ...lines].map((line) => line.map(csvCell).join(",")).join("\n");
}

export function formatMoney(value, { sign = false } = {}) {
  const amount = Number(value || 0);
  const text = Math.abs(amount).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  if (amount < 0) return `−${text}`;
  return sign && amount > 0 ? `+${text}` : text;
}
